<template>
  <div>
    <div class="space-y-6" v-if="studentInfos.length > 0">
      <div class="flex gap-3">
        <button
          class="flex items-center justify-center rounded-full bg-white w-12 h-12 !shadow disabled:!bg-gray-200"
          type="button"
          :disabled="currentIndex === 0"
          @click="moveIndexBy(-1)">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          class="flex items-center justify-center rounded-full bg-white w-12 h-12 !shadow disabled:!bg-gray-200"
          type="button"
          :disabled="currentIndex === studentInfos.length - 1"
          @click="moveIndexBy(1)">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        <div class="flex items-center justify-center gap-3 px-4 rounded-full bg-white h-12 !shadow">
          <div class="rounded-full bg-sky-600 py-1 px-3 uppercase font-bold text-white"
            v-if="currentIndex === studentInfos.length - 1">
            Current
          </div>
          <div class="rounded-full bg-slate-400 py-1 px-3 uppercase font-bold text-white"
            v-if="currentIndex !== studentInfos.length - 1"
            style="background-color: rgb(46, 55, 71);">
            Past
          </div>
          <div class="flex items-center gap-1 text-lg">
            <div>{{ startDate }}</div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M18 12H6"
              />
            </svg>
            <div>{{ endDate }}</div>
          </div>
        </div>
        <div class="flex items-center justify-center px-4 rounded-full bg-white h-12 !shadow">
          <div class="px-2"
            v-for="(weekdayInitial, index) of weekdayInitials"
            :key="`${weekdayInitial}${index}`">
            <button
              class="focus:outline-none font-bold text-white rounded-full h-10 w-10 flex items-center justify-center"
              :class="{
                'ring-2 ring-gray-700': index === currentWeekdayIndex,
              }"
              :style="{
                background: directionGradient(getDirection(index)),
              }"
              type="button"
              @click="currentWeekdayIndex = index">
              {{ weekdayInitial }}
            </button>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white !shadow rounded-md py-2 px-3 text-lg">
          <div class="text-xl font-bold mb-3">Inbound</div>
          <div class="space-y-3 text-lg mb-3"
            v-if="currentSchedule.pickup.exists">
            <div class="flex items-center gap-3">
              <div>
                <RouteDetailModal
                  :routeId="currentSchedule.pickup.routeId"
                  :title="`${currentSchedule.pickup.vehicleCode} - Inbound - ${currentSchedule.pickup.building}`"
                  :date="currentSchedule.date"
                  direction="Inbound"
                />
              </div>
              <div class="font-bold">
                Vehicle Number
              </div>
              <span>{{ currentSchedule.pickup.vehicleCode }}</span>
            </div>
            <div class="flex items-center gap-3"
              v-if="currentSchedule.pickup.arriveTime">
              <div class="font-bold">
                Arrive Time
              </div>
              <span>{{ currentSchedule.pickup.arriveTime }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Pickup Time
              </div>
              <span>{{ currentSchedule.pickup.time }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div v-if="overridePickupBuilding">
                <div class="flex flex-col gap-3 bg-gray-100 p-1">
                  <div class="flex flex-row gap-3">
                    <div class="font-bold">
                      Pickup Building
                    </div>
                    <span>{{ overridePickupBuilding.name }}</span>
                  </div>
                  <div class="flex flex-row gap-3">
                    <div class="font-bold">
                      Address
                    </div>
                    <span class="text-sm">{{ overridePickupBuilding.address }}</span>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="font-bold">
                  Pickup Address
                </div>
                <div v-if="currentSchedule.pickup.busStop">
                  <span>{{ currentSchedule.pickup.busStop }}</span>
                </div>
                <div v-else>
                  <span>{{ currentSchedule.pickup.address }}</span>
                </div>
              </div>
            </div>
            <div class="flex flex-col bg-gray-100 items-start p-1 gap-1">
              <div class="flex flex-row gap-3">
                <div class="font-bold">
                  Dropoff Building
                </div>
                <span>{{ currentSchedule.pickup.building }}</span>
              </div>
              <div class="flex flex-row gap-3"
                v-if="currentSchedule.pickup.buildingAddress">
                <div class="font-bold">
                  Address
                </div>
                <span class="text-sm">{{ currentSchedule.pickup.buildingAddress }}</span>
              </div>
            </div>
          </div>
          <div class="space-y-3 text-lg mb-3"
            v-if="!currentSchedule.pickup.exists">
            None
          </div>
        </div>
        <div class="bg-white !shadow rounded-md py-2 px-3 text-lg">
          <div class="text-xl font-bold mb-3">Outbound</div>
          <div class="space-y-3 text-lg mb-3"
            v-if="currentSchedule.dropoff.exists">
            <div class="flex items-center gap-3">
              <div>
                <RouteDetailModal
                  :routeId="currentSchedule.dropoff.routeId"
                  :title="`${currentSchedule.dropoff.vehicleCode} - Outbound - ${currentSchedule.dropoff.building}`"
                  :date="currentSchedule.date"
                  direction="Outbound"
                />
              </div>
              <div class="font-bold">
                Vehicle Number
              </div>
              <span>{{ currentSchedule.dropoff.vehicleCode }}</span>
            </div>
            <div class="flex items-center gap-3"
              v-if="currentSchedule.dropoff.departTime">
              <div class="font-bold">
                Depart Time
              </div>
              <span>{{ currentSchedule.dropoff.departTime }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div class="font-bold">
                Dropoff Time
              </div>
              <span>{{ currentSchedule.dropoff.time }}</span>
            </div>
            <div class="flex items-center gap-3">
              <div v-if="overrideDropoffBuilding">
                <div class="flex flex-col gap-3 bg-gray-100 p-1">
                  <div class="flex flex-row gap-3">
                    <div class="font-bold">
                      Dropoff Building
                    </div>
                    <span>{{ overrideDropoffBuilding.name }}</span>
                  </div>
                  <div class="flex flex-row gap-3">
                    <div class="font-bold">
                      Address
                    </div>
                    <span class="text-sm">{{ overrideDropoffBuilding.address }}</span>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="font-bold">
                  Dropoff Address
                </div>
                <div v-if="currentSchedule.dropoff.busStop">
                  <span>{{ currentSchedule.dropoff.busStop }}</span>
                </div>
                <div v-else>
                  <span>{{ currentSchedule.dropoff.address }}</span>
                </div>
              </div>
            </div>
            <div class="flex flex-col bg-gray-100 items-start p-1 gap-1">
              <div class="flex flex-row gap-3">
                <div class="font-bold">
                  Pickup Building
                </div>
                <span>{{ currentSchedule.dropoff.building }}</span>
              </div>
              <div class="flex flex-row gap-3"
                v-if="currentSchedule.dropoff.buildingAddress">
                <div class="font-bold">
                  Address
                </div>
                <span class="text-sm">{{ currentSchedule.dropoff.buildingAddress }}</span>
              </div>
            </div>
          </div>
          <div class="space-y-3 text-lg mb-3"
            v-if="!currentSchedule.dropoff.exists">
            None
          </div>
        </div>
      </div>
      <div>
        <div class="space-y-3 text-lg">
          <div class="flex items-center gap-3">
            <div class="font-bold">Grade:</div>
            <span>{{ grade }}</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="font-bold">Program:</div>
            <span>{{ program }}</span>
          </div>
          <div class="flex items-center gap-3" v-if="stateReportCodeType">
            <div class="font-bold">
              State Report Code:
            </div>
            <span>{{ `${stateReportCodeType}:${stateReportCode}` }}</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="font-bold">
              Student Release:
            </div>
            <span>{{ studentRelease }}</span>
          </div>
        </div>
      </div>
      <div class="space-y-3">
        <div>
          <div class="text-lg font-bold"
            v-if="!accommodations">
            No accommodations
          </div>
        </div>
        <div class="text-lg space-y-3" v-if="accommodations">
          <div class="font-bold">
            Accommodations:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            {{ accommodations }}
          </div>
          <div v-if="vestInfo">
            <SafetyVestInfo
              :vestInfo="vestInfo"
            />
          </div>
        </div>
        <div>
          <div class="text-lg font-bold"
            v-if="!medicalNotes">
            No medical information
          </div>
        </div>
        <div class="text-lg space-y-3" v-if="medicalNotes">
          <div class="font-bold">
            Medical Information:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            {{ medicalNotes }}
          </div>
        </div>
        <div class="text-lg font-bold"
          v-if="!additionalNotes">
          No additional transportation information
        </div>
        <div class="text-lg space-y-3" v-if="additionalNotes">
          <div class="font-bold">
            Additional Transportation Information:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            <div v-for="note in additionalNotes" :key="note.id">
              <span class="font-italic font-bold pr-2">
                {{ note.id }}
              </span>
              <span>
                {{ note.value }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Spinner
      v-if="displayLoadingStudentInfo"
      message="Loading History"
      :size="16"
    />
    <div v-if="studentInfos.length === 0 && !displayLoadingStudentInfo">None</div>
  </div>
</template>

<script>
import { map, cloneDeep, camelCase, isEmpty } from 'lodash'
import DROPDOWN_LISTS from '../DropdownLists.json'
import ACCOMMODATIONS_MAP from '../AccommodationsMap.json'

import SafetyVestInfo from './common/SafetyVestInfo.vue'
import Spinner from '../Spinner.vue'
import RouteDetailModal from '../modals/RouteDetailModal.vue'

import { getStudentInfo, getRouteInfo, getBuilding } from '../../services/api.js'
import user from '../../services/user/user.js'
import cache from '../../services/cache.js'
import time from '../../services/utils/time.js'
import address from '../../services/utils/address.js'

const weekdaySlotsTemplate = [
  {
    id: 'sun',
    display: 'Sunday',
    data: undefined,
  },
  {
    id: 'mon',
    display: 'Monday',
    data: undefined,
  },
  {
    id: 'tue',
    display: 'Tuesday',
    data: undefined,
  },
  {
    id: 'wed',
    display: 'Wednesday',
    data: undefined,
  },
  {
    id: 'thu',
    display: 'Thursday',
    data: undefined,
  },
  {
    id: 'fri',
    display: 'Friday',
    data: undefined,
  },
  {
    id: 'sat',
    display: 'Saturday',
    data: undefined,
  },
]

const weekdayInitialsTemplate = ['Su', 'M', 'T', 'W', 'R', 'F', 'Sa']

export default {
  components: {
    SafetyVestInfo,
    Spinner,
    RouteDetailModal,
  },
  props: {
    dbId: Number,
  },
  data() {
    return {
      studentInfos: [],
      currentIndex: 0,
      currentWeekdayIndex: this.getWeekdayIndex(),
      weekdayInitials: ['Su', 'M', 'T', 'W', 'R', 'F', 'Sa'],
      weekdaySlots: [],
      routeData: [],
      routeMap: {},

      displayLoadingStudentInfo: true,

      overridePickupBuilding: undefined,
      overrideDropoffBuilding: undefined,
    }
  },
  methods: {
    getWeekdayIndex() {
      const dayInt = new Date().getDay()
      if (dayInt === 0 || dayInt === 6) {
        return 0
      }
      return dayInt - 1
    },
    async getStudentInfo(dbId) {
      if (isNaN(dbId) || dbId <= 0) {
        return
      }

      try {
        this.displayLoadingStudentInfo = true
        this.studentInfos = (await getStudentInfo(dbId, {})).data
        this.displayLoadingStudentInfo = false
      }
      catch {
        this.displayLoadingStudentInfo = false
        return
      }

      this.studentInfos.map((element, index) => {
        element['id'] = index
        return element
      })
      this.currentIndex = this.studentInfos.length - 1
      if (this.currentStudentInfo) {
        this.assignScheduleData(this.currentStudentInfo.schedule.schedule_weekdays)
        this.mutateScheduleData(this.currentStudentInfo.schedule.schedule_weekdays, this.routeMap)
      }
    },
    moveIndexBy(offset) {
      this.currentIndex += offset
      if (this.currentIndex < 0) {
        this.currentIndex = 0
      }
      if (this.currentIndex >= this.studentInfos.length) {
        this.currentIndex = this.studentInfos.length - 1
      }

      if (this.currentStudentInfo) {
        this.assignScheduleData(this.currentStudentInfo.schedule.schedule_weekdays)
        this.mutateScheduleData(this.currentStudentInfo.schedule.schedule_weekdays, this.routeMap)
      }
    },
    assignScheduleData(weekdays) {
      let routeSet = new Set()
      var routeMap = {}
      for (const day of weekdays) {
        if (day.pickup_route) {
          routeMap[day.pickup_route.db_id] = day.pickup_route.number
          routeSet.add(day.pickup_route.db_id)
        }
        if (day.dropoff_route) {
          routeMap[day.dropoff_route.db_id] = day.dropoff_route.number
          routeSet.add(day.dropoff_route.db_id)
        }
      }
      let routeInfosAll = []
      for (const id of routeSet) {
        let routeInfoPromise
        if (cache.valid('getRouteInfo', id)) {
          routeInfoPromise = Promise.resolve(cache.get('getRouteInfo', id)).then(result => {
            return {
              id: id,
              number: routeMap[id],
              routeInfos: result,
            }
          })
        }
        else {
          routeInfoPromise = getRouteInfo(id).then(result => {
            cache.set(result.data, 15 * 60 * 1000, 'getRouteInfo', id)
            return {
              id: id,
              number: routeMap[id],
              routeInfos: result.data,
            }
          })
        }
        routeInfosAll.push(routeInfoPromise)
      }

      Promise.all(routeInfosAll).then(result => {
        this.routeData = result
        this.routeMap = this.createRouteMap(this.routeData)
        this.mutateScheduleData(this.currentStudentInfo.schedule.schedule_weekdays, this.routeMap)
      })

      this.weekdayInitials = cloneDeep(weekdayInitialsTemplate)

      var satIndex = weekdays.findIndex(elem => elem.weekday === 'sat')
      var sunIndex = weekdays.findIndex(elem => elem.weekday === 'sun')
      if (satIndex < 0 && sunIndex < 0) {
        this.weekdayInitials.shift()
        this.weekdayInitials.pop()
      }
    },
    mutateScheduleData(weekdays, routeMap) {
      this.weekdaySlots = cloneDeep(weekdaySlotsTemplate)
      for (var weekdayIndex = 0; weekdayIndex < this.weekdaySlots.length; ++weekdayIndex) {
        this.weekdaySlots[weekdayIndex].date = time.getDateAtWeekday(weekdayIndex)
      }
      var satIndex = weekdays.findIndex(elem => elem.weekday === 'sat')
      var sunIndex = weekdays.findIndex(elem => elem.weekday === 'sun')
      if (satIndex < 0 && sunIndex < 0) {
        this.weekdaySlots.shift()
        this.weekdaySlots.pop()
      }

      for (var i = 0; i < this.weekdaySlots.length; ++i) {
        let index = weekdays.findIndex(
          element => element.weekday === this.weekdaySlots[i].id
        )
        if (index > -1) {
          this.weekdaySlots[i].data = weekdays[index]
        }
      }

      for (const weekday of this.weekdaySlots) {
        let dropoffRoute = weekday.data?.dropoff_route
        let pickupRoute = weekday.data?.pickup_route

        if (dropoffRoute && dropoffRoute.db_id in routeMap) {
          let dropoffRouteInfo = this.selectRouteInfo(routeMap[dropoffRoute.db_id], this.currentStudentInfo.start_date, this.currentStudentInfo.end_date)
          dropoffRoute.vehicle = dropoffRouteInfo !== undefined ? dropoffRouteInfo.vehicle : { error: 'Error reading vehicle info'}
        }
        if (pickupRoute && pickupRoute.db_id in routeMap) {
          let pickupRouteInfo = this.selectRouteInfo(routeMap[pickupRoute.db_id], this.currentStudentInfo.start_date, this.currentStudentInfo.end_date)
          pickupRoute.vehicle = pickupRouteInfo !== undefined ? pickupRouteInfo.vehicle : { error: 'Error reading vehicle info'}
        }
      }
    },
    createRouteMap(routeData) {
      let routeMap = {}
      for (const routeObj of routeData) {
        if (!(routeObj.id in routeMap)) {
          routeMap[routeObj.id] = routeObj.routeInfos
        }
      }
      return routeMap
    },
    selectRouteInfo(routeInfos, startDate, endDate) {
      let sortedRouteInfos = routeInfos.sort((a, b) => {
        if (new Date(a.start_date) < new Date(b.start_date)) {
          return -1
        } else if (new Date(a.start_date) > new Date(b.start_date)) {
          return 1
        } else {
          if (new Date(a.end_date) < new Date(b.end_date)) {
            return -1
          } else if (new Date(a.end_date) > new Date(b.end_date)) {
            return 1
          } else {
            return 0
          }
        }
      })

      let relavantRouteInfos = sortedRouteInfos.filter(routeInfo => {
        let routeInfoStartDateFallsWithin = false
        let routeInfoEndDateFallsWithin = false
        if (endDate == null) {
          if (routeInfo.start_date == null) {
            console.error("Route Info has no start date. This should be impossible")
            routeInfoStartDateFallsWithin = true
          }
          else {
            routeInfoStartDateFallsWithin = true
          }
        }
        else {
          if (routeInfo.start_date == null) {
            routeInfoStartDateFallsWithin = true
          }
          else {
            routeInfoStartDateFallsWithin = new Date(routeInfo.start_date) < new Date(endDate)
          }
        }

        if (startDate == null) {
          console.error("Student Info has no start date. This should be impossible")
          if (routeInfo.end_date == null) {
            routeInfoEndDateFallsWithin = true
          }
          else {
            routeInfoEndDateFallsWithin = true
          }
        }
        else {
          if (routeInfo.end_date == null) {
            routeInfoEndDateFallsWithin = true
          }
          else {
            routeInfoEndDateFallsWithin = new Date(routeInfo.end_date) > new Date(startDate)
          }
        }

        return routeInfoStartDateFallsWithin && routeInfoEndDateFallsWithin
      })

      if (relavantRouteInfos.length > 0) {
        return relavantRouteInfos[relavantRouteInfos.length - 1]
      }
    },
    getDirection(index) {
      let direction = ''
      if (
        this.weekdaySlots[index].data?.pickup_time &&
        this.weekdaySlots[index].data?.dropoff_time
      ) {
        direction = 'B'
      } else if (this.weekdaySlots[index].data?.pickup_time) {
        direction = 'I'
      } else if (this.weekdaySlots[index].data?.dropoff_time) {
        direction = 'O'
      }
      return direction
    },

    directionGradient(direction) {
      switch (direction) {
        case 'I':
          return 'linear-gradient(to right, #0080d4 50%, rgb(200, 200, 200) 50%)'; // Left blue, right grey
        case 'O':
          return 'linear-gradient(to right, rgb(200, 200, 200) 50%, #0080d4 50%)'; // Left grey, right blue
        case 'B':
          return '#0080d4'; // Full blue
        default:
          return 'rgb(200, 200, 200)'; // Full grey
      }
      },
    },
  computed: {
    currentStudentInfo() {
      return this.studentInfos[this.currentIndex]
    },
    grade() {
      if (this.currentStudentInfo.grade) {
        return DROPDOWN_LISTS.gradeList[this.currentStudentInfo?.grade]
      } else {
        return ''
      }
    },
    program() {
      return this.currentStudentInfo?.program.description
    },
    studentRelease() {
      if (this.currentStudentInfo.student_release === 'I')
        return 'Release Independently'
      else if (this.currentStudentInfo.student_release === 'e2e')
        return 'Eye-to-Eye Release'
      else if (this.currentStudentInfo.student_release === 'h2h')
        return 'Hand-to-Hand Transfer'
      else if (this.currentStudentInfo.student_release === 'mse')
        return 'Must See Enter'
      else return 'Error'
    },
    stateReportCodeType() {
      return this.currentStudentInfo?.state_report_code_type
    },
    stateReportCode() {
      return this.currentStudentInfo?.state_report_code
    },
    address() {
      return this.currentStudentInfo?.address
    },
    pickupAddress() {
      return this.currentStudentInfo?.pickup_address
    },
    dropoffAddress() {
      return this.currentStudentInfo?.dropoff_address
    },
    additionalNotes() {
      if (this.currentStudentInfo.notes === undefined) return undefined

      let notesObj = JSON.parse(this.currentStudentInfo?.notes)
      for (const key in notesObj) {
        if (!notesObj[key]) {
          delete notesObj[key]
        }
      }
      if (!isEmpty(notesObj)) {
        return map(notesObj, (value, key) => {
          return {
            id: key,
            value: value,
          }
        })
      } else {
        return undefined
      }
    },
    medicalNotes() {
      return this.currentStudentInfo?.medical_notes
    },
    accommodations() {
      const infoNames = [
        'info_aide',
        'info_wheelchair',
        'info_solo',
        'info_enclosed_vehicle',
        'info_booster_seat',
        'info_safety_vest',
        'info_safety_belt_locking_cover',
        'info_car_seat',
      ]
      let values = []
      for (let infoName of infoNames) {
        const value = this.currentStudentInfo[infoName]
        if (value) {
          infoName = camelCase(infoName)
          if (infoName === 'infoAide') {
            if (value in ACCOMMODATIONS_MAP.infoAide) {
              values.push(ACCOMMODATIONS_MAP.infoAide[value])
            }
          } else {
            values.push(ACCOMMODATIONS_MAP[infoName])
          }
        }
      }
      if (values.length > 0) {
        return values.join(', ')
      }
      return ''
    },
    vestInfo() {
      if (!this.currentStudentInfo.vest_info) {
        return
      }
      return {
        needVestProvided: this.currentStudentInfo.vest_info['need_vest_provided'],
        manufacturer: this.currentStudentInfo.vest_info['manufacturer'],
        modelNumber: this.currentStudentInfo.vest_info['model_number'],
        vestSize: this.currentStudentInfo.vest_info['vest_size'],
      }
    },
    startDate() {
      if (this.currentStudentInfo?.start_date) {
        return new Date(this.currentStudentInfo.start_date).toLocaleString(
          'en-US',
          {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            timeZone: 'UTC',
          }
        )
      } else {
        return ''
      }
    },
    endDate() {
      if (this.currentStudentInfo?.end_date) {
        let endDateExclusive = new Date(this.currentStudentInfo.end_date)
        let endDateDisplay = new Date(endDateExclusive.getTime())
        endDateDisplay.setDate(endDateExclusive.getDate() - 1)
        return endDateDisplay.toLocaleString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          timeZone: 'UTC',
        })
      } else {
        return 'Present'
      }
    },

    currentSchedule() {
      if (this.weekdaySlots[this.currentWeekdayIndex] === undefined) {
        return {}
      }
      const ref = this.weekdaySlots[this.currentWeekdayIndex].data
      const date = this.weekdaySlots[this.currentWeekdayIndex].date

      const currentSchedule = {
        date: date,
        pickup: {
          exists: ref?.pickup_time ? true : false,
          routeId: ref?.pickup_route?.db_id,
          vehicleCode: ref?.pickup_route
            ? (ref.pickup_route.vehicle?.error ? ref.pickup_route.vehicle.error : ref.pickup_route.vehicle?.code)
            : '',
          time: ref?.pickup_time ? time.formatAMPM(ref.pickup_time) : '',
          address: ref?.pickup_address ? address.format(ref.pickup_address) : '',
          busStop: ref?.pickup_bus_stop?.description,
          building: ref?.pickup_route?.building ? ref.pickup_route.building.name : '--',
          buildingAddress: ref?.pickup_route?.building ? ref.pickup_route.building.address.apartment
              ? `${ref.pickup_route.building.address.street}, ${ref.pickup_route.building.address.apartment}, ${ref.pickup_route.building.address.city} ${ref.pickup_route.building.address.state}, ${ref.pickup_route.building.address.zip_code}`
              : `${ref.pickup_route.building.address.street}, ${ref.pickup_route.building.address.city} ${ref.pickup_route.building.address.state}, ${ref.pickup_route.building.address.zip_code}`
            : undefined,
        },
        dropoff: {
          exists: ref?.dropoff_time ? true : false,
          routeId: ref?.dropoff_route?.db_id,
          vehicleCode: ref?.dropoff_route
            ? (ref.dropoff_route.vehicle?.error ? ref.dropoff_route.vehicle.error : ref.dropoff_route.vehicle?.code)
            : '',
          time: ref?.dropoff_time ? time.formatAMPM(ref.dropoff_time) : '',
          address: ref?.dropoff_address ? address.format(ref.dropoff_address) : '',
          busStop: ref?.dropoff_bus_stop?.description,
          building: ref?.dropoff_route?.building ? ref.dropoff_route.building.name : '--',
          buildingAddress: ref?.dropoff_route?.building ? ref.dropoff_route.building.address.apartment
              ? `${ref.dropoff_route.building.address.street}, ${ref.dropoff_route.building.address.apartment}, ${ref.dropoff_route.building.address.city} ${ref.dropoff_route.building.address.state}, ${ref.dropoff_route.building.address.zip_code}`
              : `${ref.dropoff_route.building.address.street}, ${ref.dropoff_route.building.address.city} ${ref.dropoff_route.building.address.state}, ${ref.dropoff_route.building.address.zip_code}`
            : undefined,
        },
      }
      return currentSchedule
    },
  },
  async mounted() {
    await user.ready()
    await this.getStudentInfo(this.dbId)
  },
  watch: {
    dbId: async function (newValue) {
      await user.ready()
      await this.getStudentInfo(newValue)
    },
    currentSchedule() {
      if (this.currentSchedule === undefined) return

      let pickupAddressString = this.currentSchedule?.pickup?.address
      let dropoffAddressString = this.currentSchedule?.dropoff?.address

      var setOverrideBuildingPromiseThen = (attribute) => {
        return (overrideBuildings) => {
          if (overrideBuildings.length === 0) {
            this.$set(this, attribute, undefined)
          }
          else {
            let overrideBuilding = overrideBuildings[0]
            this.$set(this, attribute, {
              name: overrideBuilding.name,
              address: address.format(overrideBuilding.address),
            })
          }
        }
      }

      var makeGetBuildingApiCall = (addressString) => {
        return getBuilding({ address: addressString })
          .then(result => {
            return result.data
          }, error => {
            console.error(`Error getting building for address: ${addressString}`)
            console.error(error)
          })
      }

      var findOverrideBuilding = (addressString, overrideBuildingAttributeString) => {
        if (addressString) {
          if (cache.valid('overrideBuildings', addressString)) {
            Promise.resolve(cache.get('overrideBuildings', addressString))
              .then(setOverrideBuildingPromiseThen(overrideBuildingAttributeString))
          }
          else {
            let getBuildingPromise = makeGetBuildingApiCall(addressString)
            cache.set(getBuildingPromise, 15 * 60 * 1000, 'overrideBuildings', addressString)

            getBuildingPromise
              .then(setOverrideBuildingPromiseThen(overrideBuildingAttributeString))
          }
        }
      }

      findOverrideBuilding(pickupAddressString, 'overridePickupBuilding')
      findOverrideBuilding(dropoffAddressString, 'overrideDropoffBuilding')
    },
  },
}
</script>
